// src/pages/Admin/DossierInscription/CreateDossierInscriptionModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import { createDossierInscription } from "./../../../services/dossierServices";
import { getAllAdherentsList } from "./../../../services/adherentServices";

const todayStr = () => new Date().toISOString().slice(0, 10);

function isValidURL(u) {
  if (!u) return true;
  try {
    const url = new URL(u);
    return !!url.protocol && !!url.hostname;
  } catch {
    return false;
  }
}

// Accent/space-insensitive normalize
const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// -------- Combobox Adhérent (typeahead) ----------
function AdherentComboBox({ list, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const indexed = useMemo(() => {
    // Pré-indexation pour recherche rapide
    return (Array.isArray(list) ? list : []).map((a) => {
      const fullName = `${a?.nom || ""} ${a?.prenom || ""}`.trim();
      return {
        id: a.id,
        fullName,
        saison: a?.saison_active || "",
        _key: normalize(fullName + " " + (a?.id ?? "")),
      };
    });
  }, [list]);

  useEffect(() => {
    // Si value existe, on set l'input à ce libellé
    const selected = indexed.find((r) => String(r.id) === String(value));
    if (selected && !open) {
      setQuery(selected.fullName);
    } else if (!value && !open) {
      setQuery("");
    }
  }, [value, indexed, open]);

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return indexed.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    const filtered = indexed.filter((r) =>
      tokens.every((t) => r._key.includes(t))
    );
    return filtered.slice(0, 100); // cap affichage
  }, [query, indexed]);

  useEffect(() => {
    setFocusIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    const active = el.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: "nearest" });
    }
  }, [focusIndex, open]);

  const selectIndex = (i) => {
    const row = results[i];
    if (!row) return;
    onChange?.(row.id);
    setQuery(row.fullName);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      setFocusIndex((i) => Math.min(i + 1, results.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setFocusIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      selectIndex(focusIndex);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      e.preventDefault();
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          placeholder="Rechercher un adhérent (nom, prénom, #id)…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
        />
        {value ? (
          <button
            type="button"
            onClick={() => {
              onChange?.("");
              setQuery("");
              setOpen(true);
              inputRef.current?.focus();
            }}
            className="rounded-xl px-3 py-2 text-sm ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            title="Effacer la sélection"
          >
            Effacer
          </button>
        ) : null}
      </div>

      {open && (
        <div
          className="absolute z-50 mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-64 overflow-auto"
          ref={listRef}
          role="listbox"
          aria-label="Choisir un adhérent"
          onMouseDown={(e) => e.preventDefault()} // évite blur avant click
        >
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              Aucun résultat
            </div>
          ) : (
            results.map((r, i) => {
              const active = i === focusIndex;
              return (
                <div
                  key={r.id}
                  data-active={active ? "true" : undefined}
                  role="option"
                  aria-selected={String(String(r.id) === String(value))}
                  onMouseEnter={() => setFocusIndex(i)}
                  onClick={() => selectIndex(i)}
                  className={`px-3 py-2 cursor-pointer text-sm ${
                    active
                      ? "bg-violet-50 dark:bg-slate-800/60"
                      : "bg-transparent"
                  }`}
                >
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    {r.fullName}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    ID #{r.id}
                    {r.saison ? ` • Saison ${r.saison}` : ""}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// --------- Carte de section ----------
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h4>
        {subtitle && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {subtitle}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

// --------- Checkbox XL ----------
function BigCheck({ name, checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 px-4 py-3 bg-white/90 dark:bg-slate-900/40">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
      />
      <span className="text-sm font-medium">{label}</span>
    </label>
  );
}

export default function CreateDossierInscriptionModal({
  isOpen,
  onClose,
  refreshList,
}) {
  const [formData, setFormData] = useState({
    inscription_adherent_id: "",
    cm_url: "",
    date_cm: "",
    validate_cm: false,
    formulaire_licence_a_signer: "",
    formulaire_licence_signe: "",
    licence_url: "",
    validate_licence: false,
    validate_reglement: false,
    lien_drive: "",
  });

  const [adherents, setAdherents] = useState([]);
  const [loadingAdherents, setLoadingAdherents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Chargement adhérents à l'ouverture
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      inscription_adherent_id: "",
      cm_url: "",
      date_cm: "",
      validate_cm: false,
      formulaire_licence_a_signer: "",
      formulaire_licence_signe: "",
      licence_url: "",
      validate_licence: false,
      validate_reglement: false,
      lien_drive: "",
    });

    (async () => {
      try {
        setLoadingAdherents(true);
        const list = await getAllAdherentsList();
        const rows = Array.isArray(list) ? list : [];
        rows.sort((a, b) =>
          `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
            `${b?.nom || ""} ${b?.prenom || ""}`,
            "fr"
          )
        );
        setAdherents(rows);
      } catch (e) {
        console.error("Erreur lors du chargement des adhérents:", e);
        toast.error("Erreur lors du chargement des adhérents.");
      } finally {
        setLoadingAdherents(false);
      }
    })();
  }, [isOpen]);

  const onChangeField = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "validate_cm") {
      // Si on coche CM validé sans date, on remplit à aujourd’hui
      setFormData((p) => ({
        ...p,
        validate_cm: checked,
        date_cm: checked && !p.date_cm ? todayStr() : p.date_cm,
      }));
      return;
    }
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!formData.inscription_adherent_id) return false;
    // URLs valides si renseignées
    const urls = [
      formData.cm_url,
      formData.formulaire_licence_a_signer,
      formData.formulaire_licence_signe,
      formData.licence_url,
      formData.lien_drive,
    ];
    if (urls.some((u) => !isValidURL(u))) return false;
    return true;
  }, [formData, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Validation basique des URLs (vide OK)
    const badField = [
      "cm_url",
      "formulaire_licence_a_signer",
      "formulaire_licence_signe",
      "licence_url",
      "lien_drive",
    ].find((k) => !isValidURL(formData[k]));
    if (badField) {
      toast.error(
        "Veuillez saisir des URLs valides (https://…) ou laisser vide."
      );
      return;
    }

    try {
      setSubmitting(true);

      const id = Number(formData.inscription_adherent_id);

      // Cas "minimal" : uniquement l'adhérent renseigné, le reste vide/non coché
      const onlyId =
        !formData.cm_url &&
        !formData.date_cm &&
        !formData.formulaire_licence_a_signer &&
        !formData.formulaire_licence_signe &&
        !formData.licence_url &&
        !formData.lien_drive &&
        !formData.validate_cm &&
        !formData.validate_licence &&
        !formData.validate_reglement;

      if (onlyId) {
        // ➜ n’envoie QUE l’ID (ton service ajoutera les 3 booléens à false)
        await createDossierInscription(id);
      } else {
        // ➜ envoie un payload nettoyé (vides -> null)
        const payload = {
          cm_url: orNull(formData.cm_url),
          date_cm: formData.date_cm ? formData.date_cm : null,
          validate_cm: !!formData.validate_cm,
          formulaire_licence_a_signer: orNull(
            formData.formulaire_licence_a_signer
          ),
          formulaire_licence_signe: orNull(formData.formulaire_licence_signe),
          licence_url: orNull(formData.licence_url),
          validate_licence: !!formData.validate_licence,
          validate_reglement: !!formData.validate_reglement,
          lien_drive: orNull(formData.lien_drive),
        };

        // si CM coché sans date -> aujourd’hui
        if (payload.validate_cm && !payload.date_cm) {
          payload.date_cm = todayStr();
        }

        await createDossierInscription(id, payload);
      }

      toast.success("Dossier d'inscription créé avec succès.");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la création du dossier", error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erreur lors de la création du dossier.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const orNull = (v) => {
    const t = (v ?? "").toString().trim();
    return t === "" ? null : t;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal card */}
      <div
        className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 shrink-0">
          {" "}
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Créer un dossier d’inscription
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Trouvez l’adhérent, puis complétez les sections. Validez en bas.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto overscroll-contain p-5 sm:p-6 space-y-5"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Sélecteur adhérent */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Adhérent <span className="text-rose-500">*</span>
            </label>
            <AdherentComboBox
              list={adherents}
              value={formData.inscription_adherent_id}
              onChange={(id) =>
                setFormData((p) => ({
                  ...p,
                  inscription_adherent_id: Number(id), // <- nombre, pas string
                }))
              }
              disabled={loadingAdherents}
            />
            {formData.inscription_adherent_id ? (
              <p className="mt-1 text-[11px] text-slate-500">
                ID sélectionné : #{formData.inscription_adherent_id}
              </p>
            ) : null}
          </div>

          {/* Bloc 1 — Certificat Médical */}
          <SectionCard
            title="1) Certificat médical"
            subtitle="Renseignez l’URL (facultatif) et la date du certificat."
          >
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                URL du Certificat Médical
              </label>
              <input
                type="url"
                name="cm_url"
                placeholder="https://…"
                value={formData.cm_url}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Date du certificat médical
              </label>
              <input
                type="date"
                name="date_cm"
                value={formData.date_cm}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </SectionCard>

          {/* Bloc 2 — Licence & Assurance */}
          <SectionCard
            title="2) Licence & Assurance"
            subtitle="Liens vers les formulaires et la licence (facultatifs)."
          >
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Formulaire à signer (URL)
              </label>
              <input
                type="url"
                name="formulaire_licence_a_signer"
                placeholder="https://…"
                value={formData.formulaire_licence_a_signer}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Formulaire signé (URL)
              </label>
              <input
                type="url"
                name="formulaire_licence_signe"
                placeholder="https://…"
                value={formData.formulaire_licence_signe}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Licence (URL)
              </label>
              <input
                type="url"
                name="licence_url"
                placeholder="https://…"
                value={formData.licence_url}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </SectionCard>

          {/* Bloc 3 — Règlement */}
          <SectionCard
            title="3) Dossier Google Drive"
            subtitle="Lien de dossier (Drive) si besoin."
          >
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Lien Drive (URL)
              </label>
              <input
                type="url"
                name="lien_drive"
                placeholder="https://…"
                value={formData.lien_drive}
                onChange={onChangeField}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </SectionCard>

          {/* Validations — grosses cases à cocher */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
              Validations
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <BigCheck
                name="validate_cm"
                label="Certificat médical validé"
                checked={formData.validate_cm}
                onChange={onChangeField}
              />
              <BigCheck
                name="validate_licence"
                label="Licence validée"
                checked={formData.validate_licence}
                onChange={onChangeField}
              />
              <BigCheck
                name="validate_reglement"
                label="Règlement validé"
                checked={formData.validate_reglement}
                onChange={onChangeField}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 -mx-5 sm:-mx-6 px-5 sm:px-6 pb-1 pt-3 bg-white/90 dark:bg-slate-900/80 backdrop-blur border-t border-slate-200/70 dark:border-slate-700/70 flex justify-end gap-2">
            {" "}
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${
                !canSubmit ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
